import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Building2,
  Briefcase,
  Calendar,
  DollarSign,
  Edit,
  User,
  Users,
  FileText,
  Percent,
} from 'lucide-react';
import { fetchPlacement, fetchInvoicesForPlacement } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PlacementForm } from '@/components/placements/PlacementForm';
import { formatCurrency, formatDate, capitalizeFirst } from '@/lib/utils';

function getStatusBadgeVariant(status: string): 'default' | 'secondary' | 'success' | 'warning' | 'destructive' {
  switch (status) {
    case 'active':
      return 'success';
    case 'completed':
      return 'default';
    case 'cancelled':
      return 'destructive';
    default:
      return 'secondary';
  }
}

function getCommissionStatusVariant(status: string): 'default' | 'secondary' | 'success' | 'warning' | 'destructive' {
  switch (status) {
    case 'paid':
      return 'success';
    case 'pending':
      return 'warning';
    case 'cancelled':
      return 'destructive';
    default:
      return 'secondary';
  }
}

function PlacementDetailSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Skeleton className="h-8 w-8 rounded" />
        <Skeleton className="h-6 w-32" />
      </div>
      <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen p-6">
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-5 w-48" />
          <div className="flex gap-2">
            <Skeleton className="h-6 w-20" />
            <Skeleton className="h-6 w-20" />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Skeleton className="h-64 lg:col-span-2" />
        <Skeleton className="h-64" />
      </div>
    </div>
  );
}

export function PlacementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showEditDialog, setShowEditDialog] = useState(false);

  const {
    data: placement,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['placement', id],
    queryFn: () => fetchPlacement(id!),
    enabled: !!id,
  });

  const { data: placementInvoicesData } = useQuery({
    queryKey: ['invoices', 'placement', id],
    queryFn: () => fetchInvoicesForPlacement(id!),
    enabled: !!id,
  });
  const placementInvoices = placementInvoicesData?.data ?? [];

  if (isLoading) {
    return <PlacementDetailSkeleton />;
  }

  if (error || !placement) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-text-primary">Placement Not Found</h2>
          <p className="text-text-tertiary mt-2">
            The placement you are looking for does not exist or has been removed.
          </p>
          <Button asChild className="mt-4">
            <Link to="/placements">Back to Placements</Link>
          </Button>
        </div>
      </div>
    );
  }

  const currency = placement.salaryCurrency || 'ZAR';

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div>
        <Link
          to="/placements"
          className="inline-flex items-center gap-2 text-sm text-text-tertiary hover:text-text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Placements
        </Link>
      </div>

      {/* Placement Header */}
      <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen p-6">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
                <Briefcase className="h-7 w-7 text-emerald-500" />
                Placement: {placement.candidate.firstName} {placement.candidate.lastName}
              </h1>
              <Badge variant={getStatusBadgeVariant(placement.status)}>
                {capitalizeFirst(placement.status)}
              </Badge>
            </div>

            <div className="flex flex-wrap gap-4 mt-4 text-sm text-text-tertiary">
              <div className="flex items-center gap-1">
                <User className="h-4 w-4" />
                <Link
                  to={`/candidates/${placement.candidateId}`}
                  className="hover:text-emerald-500"
                >
                  {placement.candidate.firstName} {placement.candidate.lastName}
                </Link>
              </div>
              <div className="flex items-center gap-1">
                <Briefcase className="h-4 w-4" />
                <Link
                  to={`/jobs/${placement.jobId}`}
                  className="hover:text-emerald-500"
                >
                  {placement.job.title}
                </Link>
              </div>
              <div className="flex items-center gap-1">
                <Building2 className="h-4 w-4" />
                <Link
                  to={`/companies/${placement.companyId}`}
                  className="hover:text-emerald-500"
                >
                  {placement.company.name}
                </Link>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowEditDialog(true)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Placement Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Placement Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <dt className="text-sm font-medium text-text-tertiary flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    Start Date
                  </dt>
                  <dd className="text-text-primary mt-1">
                    {placement.startDate ? formatDate(placement.startDate) : 'Not set'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-text-tertiary flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    End Date
                  </dt>
                  <dd className="text-text-primary mt-1">
                    {placement.endDate ? formatDate(placement.endDate) : 'Not set'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-text-tertiary flex items-center gap-1">
                    <DollarSign className="h-4 w-4" />
                    Salary
                  </dt>
                  <dd className="text-text-primary mt-1">
                    {placement.salary != null
                      ? formatCurrency(placement.salary, currency)
                      : 'Not specified'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-text-tertiary flex items-center gap-1">
                    <Percent className="h-4 w-4" />
                    Fee Type
                  </dt>
                  <dd className="text-text-primary mt-1">
                    {placement.feeType === 'percentage' ? 'Percentage' : 'Fixed Fee'}
                  </dd>
                </div>
                {placement.feeType === 'percentage' && placement.feePercentage != null && (
                  <div>
                    <dt className="text-sm font-medium text-text-tertiary">Fee Percentage</dt>
                    <dd className="text-text-primary mt-1">{placement.feePercentage}%</dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-text-tertiary flex items-center gap-1">
                    <DollarSign className="h-4 w-4" />
                    Fee Amount
                  </dt>
                  <dd className="text-text-primary mt-1 text-lg font-semibold text-emerald-500">
                    {placement.feeAmount != null
                      ? formatCurrency(placement.feeAmount, currency)
                      : 'Not calculated'}
                  </dd>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Commission Splits */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5" />
                Commission Splits
              </CardTitle>
            </CardHeader>
            <CardContent>
              {placement.commissions && placement.commissions.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Team Member</TableHead>
                      <TableHead className="text-right">Split %</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {placement.commissions.map((commission) => (
                      <TableRow key={commission.id}>
                        <TableCell className="font-medium">
                          {commission.user.firstName} {commission.user.lastName}
                        </TableCell>
                        <TableCell className="text-right">
                          {commission.splitPercentage}%
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(commission.amount, currency)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={getCommissionStatusVariant(commission.status)}>
                            {capitalizeFirst(commission.status)}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="flex flex-col items-center justify-center h-24 text-text-tertiary">
                  <Users className="h-8 w-8 mb-2" />
                  <p>No commission splits configured</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          {placement.notes && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-text-secondary whitespace-pre-wrap">{placement.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Related Info */}
        <div className="space-y-6">
          {/* Candidate Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5" />
                Candidate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Link
                to={`/candidates/${placement.candidateId}`}
                className="block p-3 bg-bg-secondary rounded-lg hover:bg-white/5 transition-colors"
              >
                <div className="font-medium text-text-primary">
                  {placement.candidate.firstName} {placement.candidate.lastName}
                </div>
              </Link>
            </CardContent>
          </Card>

          {/* Job Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                Job
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Link
                to={`/jobs/${placement.jobId}`}
                className="block p-3 bg-bg-secondary rounded-lg hover:bg-white/5 transition-colors"
              >
                <div className="font-medium text-text-primary">{placement.job.title}</div>
                <div className="text-sm text-text-tertiary mt-1">{placement.company.name}</div>
              </Link>
            </CardContent>
          </Card>

          {/* Company Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Company
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Link
                to={`/companies/${placement.companyId}`}
                className="block p-3 bg-bg-secondary rounded-lg hover:bg-white/5 transition-colors"
              >
                <div className="font-medium text-text-primary">{placement.company.name}</div>
              </Link>
            </CardContent>
          </Card>

          {/* Linked Invoices */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Invoices ({placementInvoices.length})
                </CardTitle>
                <Button
                  size="sm"
                  onClick={() => navigate(`/invoices?placementId=${placement.id}`)}
                >
                  Create Invoice
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {placementInvoices.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Number</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {placementInvoices.map((inv) => (
                      <TableRow
                        key={inv.id}
                        onClick={() => navigate(`/invoices/${inv.id}`)}
                        className="cursor-pointer hover:bg-white/5"
                      >
                        <TableCell className="font-medium">{inv.invoiceNumber}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{capitalizeFirst(inv.status)}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(inv.totalAmount, inv.currency || currency)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-sm text-text-tertiary py-4">
                  No invoices for this placement yet.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-text-tertiary" />
                  <div>
                    <dt className="text-sm font-medium text-text-tertiary">Created</dt>
                    <dd className="text-text-primary">{formatDate(placement.createdAt)}</dd>
                  </div>
                </div>
              </dl>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Placement Dialog */}
      <PlacementForm
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        onSuccess={() => {
          setShowEditDialog(false);
        }}
        placementId={placement.id}
        candidateId={placement.candidateId}
        jobId={placement.jobId}
        companyId={placement.companyId}
      />
    </div>
  );
}
